import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import * as React from 'react'
import { href, Link, redirect, useFetcher, useLoaderData, useOutletContext, type LoaderFunctionArgs, type MetaFunction } from 'react-router'
import { ProductMainImage, ProductSidebarThumbnail } from '~/components/ui/optimized-image'
import Button from '~/components/webshop-button'
import type { LayoutContext } from '~/routes/_layout'
import { getTenantContext } from '~/services/authz.server'
import { getDeliveryCostsByOrgId } from '~/services/delivery-costs.server'
import { getOrganizationDetailsByOrgId } from '~/services/org-details.server'
import { getProductCached } from '~/services/products.cache.server'
import { getPriceColorClass, formatPriceDifference } from '~/utils/pricing'
import { useShopContext } from '~/utils/use-shop-context'
import { ProductDetails } from './components/product-details'

export async function loader(args: LoaderFunctionArgs) {
  const slug = args.params.slug
  const tc = await getTenantContext(args)

  // Build canonical URL from request (domain-specific for multi-tenant)
  const url = new URL(args.request.url)
  const canonicalUrl = `${url.origin}/product/${slug}`

  // B2B products should be visible to admins and webshop owners
  const canSeeB2B = tc.isAdmin || tc.orgRole === 'webshop_owner'

  const [product, orgDetails, deliverySettings] = await Promise.all([
    getProductCached({ slug: slug, organizationId: tc.org?.id, includingB2B: canSeeB2B }),
    getOrganizationDetailsByOrgId(tc.org?.id),
    getDeliveryCostsByOrgId(tc.org?.id ?? null)
  ])

  if (!slug || !product) {
    return redirect(href('/'))
  }
  return { product, orgDetails, canSeeB2B, deliverySettings, canonicalUrl }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const orgName = data?.orgDetails.companyName
  const p = data?.product
  const title = `${p?.title} – ${orgName} webshop`
  const description = p?.summary
  const image = p?.images?.[0]?.url
  const canonicalUrl = data?.canonicalUrl

  // JSON-LD Product schema for AI/LLM search optimization
  const productJsonLd = p
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: p.title,
        description: p.summary,
        image: image,
        sku: p.sku,
        ...(p.ean ? { gtin13: p.ean } : {}),
        offers: {
          '@type': 'Offer',
          price: p.price?.amount,
          priceCurrency: 'EUR',
          availability: p.soldOut ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
          url: canonicalUrl,
          seller: {
            '@type': 'Organization',
            name: orgName
          }
        },
        brand: {
          '@type': 'Organization',
          name: orgName
        }
      }
    : null

  return [
    { title },
    { name: 'description', content: description },
    // Canonical URL
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    // OpenGraph tags
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'product' },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:site_name', content: `${orgName} webshop` },
    { property: 'og:locale', content: 'nl_NL' },
    ...(image ? [{ property: 'og:image', content: image }] : []),
    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    ...(image ? [{ name: 'twitter:image', content: image }] : []),
    // JSON-LD structured data
    ...(productJsonLd ? [{ 'script:ld+json': productJsonLd }] : [])
  ]
}

function formatDeliveryTime(deadlineTime: string, days: number): string {
  const dayText = days === 0 ? 'vandaag' : days === 1 ? 'morgen' : `over ${days} dagen`
  return `Voor ${deadlineTime} uur besteld, ${dayText} in huis.`
}

function StatusNote({ tone, children }: { tone: 'info' | 'warn' | 'error'; children: React.ReactNode }) {
  const toneClasses = {
    info: 'border-(--ws-link) bg-(--ws-link-tint) text-(--ws-link)',
    warn: 'border-amber-300 bg-amber-50 text-amber-800',
    error: 'border-red-300 bg-red-50 text-red-800'
  }[tone]

  return (
    <div className={`mb-4 rounded-(--ws-radius-inner) border p-3 text-sm ${toneClasses}`}>
      <span className="font-semibold">{children}</span>
    </div>
  )
}

export default function Product() {
  const { product, deliverySettings } = useLoaderData<typeof loader>()
  const fetcher = useFetcher<{ ok: boolean; error?: string }>()
  const { snapshot, products, productPricing, selectedB2BCustomer, categoriesEnabled, isShopOpen, canBypassClosedShop } =
    useOutletContext<LayoutContext>()
  const { isB2B } = useShopContext()

  const entry = snapshot.items.find(i => i.product_id === String(product.id))
  const inCart = !!entry

  // Image gallery state
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0)
  const images = product.images ?? []
  const hasImages = images.length > 0
  const hasMultipleImages = images.length > 1
  const selectedImage = images[selectedImageIndex]

  const isSubmittingAdd =
    fetcher.state !== 'idle' && fetcher.formData?.get('_intent') === 'add' && fetcher.formData?.get('product_id') === String(product.id)

  // Get pricing data for this product
  const pricing = productPricing.get(product.id)
  const displayPrice = pricing?.sellingPrice ?? product.price?.amount ?? 0
  const priceColorClass = pricing ? getPriceColorClass(pricing.priceType) : ''
  const showPriceDiff = pricing && pricing.priceType !== 'default' && pricing.percentageDiff !== null

  const priceCents = Math.round(displayPrice * 100)

  // Admins/webshop owners must select a B2B customer before adding products
  // This is because prices can vary per customer
  const needsB2BCustomer = isB2B && !selectedB2BCustomer

  // Products awaiting first stock cannot be added to cart (admin only sees these)
  const awaitingStock = (product as typeof product & { awaitingStock?: boolean }).awaitingStock ?? false

  // Shop closed for regular customers (admins/owners can bypass)
  const shopClosedForUser = !isShopOpen && !canBypassClosedShop

  // Sold out products and products awaiting stock cannot be added to cart
  const canAddToCart = !product.soldOut && !needsB2BCustomer && !awaitingStock && !shopClosedForUser

  // Check for errors from the cart action (out of stock from fulfillment service)
  const addFailed = fetcher.data && !fetcher.data.ok && fetcher.state === 'idle'
  const cartError = addFailed ? fetcher.data?.error : null

  const otherProducts = (products ?? []).filter(p => p.id !== product.id).slice(0, 6)

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-1">
      <div className="w-full p-4 lg:p-2 lg:pt-4">
        {/* Breadcrumb Navigation */}
        <nav className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm font-medium text-(--ws-link) transition-colors duration-200 hover:text-(--ws-link-hover)">
            <ChevronLeftIcon className="h-4 w-4" />
            Terug naar overzicht
          </Link>
        </nav>

        <div className="flex w-full flex-col-reverse justify-between gap-6 md:flex-row">
          {/* Sidebar: andere producten — alleen tonen als die er zijn */}
          {otherProducts.length > 0 && (
            <aside className="w-full md:w-32 md:flex-shrink-0">
              <h2 className="mb-4 text-lg font-bold text-(--ws-heading) md:sr-only">Andere producten</h2>
              <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-1 md:gap-3">
                {otherProducts.map(p => {
                  const thumbUrl = p.images?.[0]?.url
                  const hasThumb = !!thumbUrl
                  return (
                    <li key={p.id}>
                      <Link to={href(`/product/:slug`, { slug: p.slug })} className="group ws-card-interactive block overflow-hidden p-2">
                        {hasThumb ? (
                          <ProductSidebarThumbnail
                            src={thumbUrl}
                            alt={p.title}
                            className="ease-soft w-full rounded-(--ws-radius-inner) transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex aspect-square w-full items-center justify-center rounded-(--ws-radius-inner) bg-(--ws-surface-2)">
                            <svg className="h-8 w-8 opacity-30" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </aside>
          )}

          {/* Main Product */}
          <main className="flex-1">
            <article className="ws-card p-6 md:p-10">
              {/* Product Header */}
              <header className="mb-6">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-title text-balance text-(--ws-heading)">{product.title}</h1>
                  {product.b2b && (
                    <span className="rounded-full border border-(--ws-link) bg-(--ws-link-tint) px-3 py-1 text-xs font-semibold text-(--ws-link)">
                      B2B
                    </span>
                  )}
                </div>
                <p className="mt-2 text-base text-(--ws-text-muted) sm:hidden">{product.summary}</p>
              </header>

              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-10">
                {/* Image Gallery — sticky op desktop zodat het beeld meeloopt bij lange productinfo */}
                <div className="sm:sticky sm:top-24 sm:self-start">
                  {hasImages ? (
                    <div className="group relative aspect-square overflow-hidden rounded-(--ws-radius-inner) bg-white">
                      {/* Main image - centered in fixed aspect ratio container */}
                      <div
                        key={selectedImage.url}
                        className="absolute inset-0 flex items-center justify-center p-4 motion-safe:animate-[ws-fade-in_200ms_var(--ease-soft)]">
                        <ProductMainImage
                          src={selectedImage.url}
                          alt={selectedImage.alt || product.title}
                          className="max-h-full max-w-full object-contain"
                          priority
                        />
                      </div>

                      {/* Navigation arrows for multiple images */}
                      {hasMultipleImages && (
                        <>
                          <button
                            type="button"
                            onClick={() => setSelectedImageIndex(i => (i === 0 ? images.length - 1 : i - 1))}
                            className="shadow-card hover:shadow-card-hover absolute top-1/2 left-3 z-10 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-800 opacity-0 backdrop-blur-sm transition-all duration-200 group-hover:opacity-100 hover:bg-white focus-visible:opacity-100"
                            aria-label="Vorige afbeelding">
                            <ChevronLeftIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedImageIndex(i => (i === images.length - 1 ? 0 : i + 1))}
                            className="shadow-card hover:shadow-card-hover absolute top-1/2 right-3 z-10 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-800 opacity-0 backdrop-blur-sm transition-all duration-200 group-hover:opacity-100 hover:bg-white focus-visible:opacity-100"
                            aria-label="Volgende afbeelding">
                            <ChevronRightIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}

                      {/* Image counter */}
                      {hasMultipleImages && (
                        <div className="absolute right-3 bottom-3 z-10 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white tabular-nums backdrop-blur-sm">
                          {selectedImageIndex + 1} / {images.length}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex aspect-square w-full items-center justify-center rounded-(--ws-radius-inner) bg-(--ws-surface-2)">
                      <svg className="h-20 w-20 opacity-30" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}

                  {/* Thumbnail navigation */}
                  {hasMultipleImages && (
                    <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                      {images.map((img, idx) => (
                        <button
                          key={img.url}
                          type="button"
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`h-16 w-16 flex-shrink-0 overflow-hidden rounded-(--ws-radius-inner) border-2 transition-all duration-200 ${
                            idx === selectedImageIndex ? 'border-(--ws-link)' : 'border-transparent opacity-60 hover:opacity-100'
                          }`}>
                          <img src={img.url} alt={img.alt || `${product.title} - afbeelding ${idx + 1}`} className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="flex flex-col">
                  <p className="mb-6 hidden max-w-prose text-base leading-relaxed text-(--ws-text-muted) sm:block">{product.summary}</p>

                  <div>
                    {/* Price */}
                    <div className="mb-6">
                      <div className={`text-4xl font-bold tracking-tight tabular-nums ${priceColorClass || 'text-(--ws-heading)'}`}>
                        € {displayPrice.toFixed(2)}
                        {showPriceDiff && (
                          <span className="ml-2 text-base font-normal tracking-normal text-(--ws-text-muted)">
                            ({formatPriceDifference(pricing.percentageDiff)})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Add to Cart */}
                    <fetcher.Form method="post" action={href('/cart')} className="mb-4">
                      <input type="hidden" name="_intent" value="add" />
                      <input type="hidden" name="product_id" value={product.id} />
                      <input type="hidden" name="qty" value="1" />
                      <input type="hidden" name="title" value={product.title} />
                      <input type="hidden" name="price_cents" value={priceCents} />

                      <div title={shopClosedForUser ? 'Deze webshop is momenteel gesloten' : needsB2BCustomer ? 'Selecteer eerst een B2B klant' : ''}>
                        <Button
                          bgColor={addFailed ? '#dc2626' : undefined}
                          bgColorHover={addFailed ? '#b91c1c' : undefined}
                          isDone={inCart}
                          loading={isSubmittingAdd}
                          type="submit"
                          disabled={isSubmittingAdd || !canAddToCart || addFailed}
                          aria-label={`Toevoegen aan winkelwagen: ${product.title}`}>
                          {addFailed ? (
                            <span>Uitverkocht</span>
                          ) : shopClosedForUser ? (
                            <span>Webshop gesloten</span>
                          ) : awaitingStock ? (
                            <span>Binnenkort leverbaar</span>
                          ) : product.soldOut ? (
                            <span>Uitverkocht</span>
                          ) : needsB2BCustomer ? (
                            <span>Selecteer B2B klant</span>
                          ) : isSubmittingAdd ? (
                            <span>Toevoegen</span>
                          ) : inCart ? (
                            <span className="flex items-center justify-center gap-x-2">
                              <CheckIcon className="w-5" /> In winkelwagen
                            </span>
                          ) : (
                            <span>In winkelwagen</span>
                          )}
                        </Button>
                      </div>
                      {cartError && <p className="mt-2 text-sm text-red-600">{cartError}</p>}
                    </fetcher.Form>

                    {/* Status Messages */}
                    {shopClosedForUser && <StatusNote tone="warn">Deze webshop is momenteel gesloten voor bestellingen.</StatusNote>}

                    {needsB2BCustomer && !shopClosedForUser && (
                      <StatusNote tone="info">Selecteer eerst een B2B klant om producten toe te voegen.</StatusNote>
                    )}

                    {awaitingStock && <StatusNote tone="info">Dit product wacht op de eerste levering en kan nog niet worden besteld.</StatusNote>}

                    {product.soldOut && !awaitingStock && (
                      <StatusNote tone="warn">Dit product is uitverkocht en kan niet worden toegevoegd aan de winkelwagen.</StatusNote>
                    )}

                    {cartError && <StatusNote tone="error">{cartError}</StatusNote>}

                    {/* Stock Status */}
                    {!product.soldOut && !awaitingStock && (
                      <div className="mb-6 flex flex-wrap items-center gap-3 text-sm">
                        <span className="rounded-(--ws-radius-inner) bg-(--ws-success-tint) px-2 py-1 text-xs font-bold whitespace-nowrap text-(--ws-success)">
                          Op voorraad
                        </span>
                        <span className="flex items-center gap-1 text-xs text-(--ws-text-muted)">
                          {formatDeliveryTime(deliverySettings.delivery_deadline_time, deliverySettings.delivery_days)}
                          <InformationCircleIcon
                            className="h-4 w-4 flex-shrink-0 cursor-help"
                            title="Dit is een geschatte leverdatum. De werkelijke leverdatum kan afwijken."
                          />
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="mt-auto">
                    <ProductDetails
                      details={product.details}
                      ean={product.ean}
                      category={product.category.label}
                      categoryKey={product.category.key}
                      categoriesEnabled={categoriesEnabled}
                    />
                  </div>
                </div>
              </div>
            </article>
          </main>
        </div>
      </div>
    </div>
  )
}
